<template>
  <el-dialog
      v-model="dialogVisible"
      title="绑定手机号"
      width="400px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
  >
    <el-form :model="bindForm" ref="bindFormRef">
      <el-form-item prop="phone">
        <el-input
            v-model="bindForm.phone"
            placeholder="请输入手机号"
            size="large"
            clearable
            @input="clearPhoneError"
        >
          <template #prefix>
            <el-icon><Iphone /></el-icon>
          </template>
        </el-input>
        <div v-if="phoneError" class="error-message">
          <el-icon><WarningFilled /></el-icon>
          <span>{{ phoneError }}</span>
        </div>
      </el-form-item>

      <el-form-item prop="captcha">
        <el-input
            v-model="bindForm.captcha"
            placeholder="请输入验证码"
            size="large"
            @input="clearCaptchaError"
        >
          <template #prefix>
            <el-icon><Message /></el-icon>
          </template>
          <template #suffix>
            <el-button
                link
                class="captcha-btn"
                :disabled="captchaCooldown > 0"
                @click="sendCaptcha"
            >
              {{ captchaCooldown > 0 ? `${captchaCooldown}秒后重试` : '获取验证码' }}
            </el-button>
          </template>
        </el-input>
        <div v-if="captchaError" class="error-message">
          <el-icon><WarningFilled /></el-icon>
          <span>{{ captchaError }}</span>
        </div>
        <div v-if="captchaSuccess" class="success-message">
          <el-icon><SuccessFilled /></el-icon>
          <span>{{ captchaSuccess }}</span>
        </div>
      </el-form-item>
    </el-form>

    <!-- 微信绑定提示 -->
    <div class="wechat-bind-tip">
      <p>绑定成功后，系统将自动为您创建账号</p>
    </div>

    <template #footer>
      <el-button
          type="primary"
          @click="handleBind"
          :loading="loading"
          style="width: 100%"
      >
        绑定手机
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Iphone, Message, WarningFilled, SuccessFilled } from '@element-plus/icons-vue'
import request from '@/utils/request'
import { eventBus } from '@/utils/eventBus'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  sessionId: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'bind-success'])

const dialogVisible = ref(false)
const bindForm = reactive({
  phone: '',
  captcha: ''
})
const captchaCooldown = ref(0)
const loading = ref(false)
const phoneError = ref('')
const captchaError = ref('')
const captchaSuccess = ref('')

// 监听modelValue变化
watch(() => props.modelValue, (newVal) => {
  dialogVisible.value = newVal
})

// 监听dialogVisible变化
watch(dialogVisible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 清除手机号错误
const clearPhoneError = () => {
  phoneError.value = ''
}

// 清除验证码错误
const clearCaptchaError = () => {
  captchaError.value = ''
  captchaSuccess.value = ''
}

// 发送验证码
const sendCaptcha = async () => {
  clearPhoneError()
  clearCaptchaError()

  if (!bindForm.phone) {
    phoneError.value = '请输入手机号'
    return
  }

  if (!/^1[3-9]\d{9}$/.test(bindForm.phone)) {
    phoneError.value = '手机号格式不正确'
    return
  }

  try {
    const response = await request.get('/auth/verify-code/phone', {
      params: {
        phone: bindForm.phone,
        scene: 'Bind'
      }
    })

    if (response.code === '200') {
      captchaSuccess.value = '验证码已发送'
      // 开始倒计时
      captchaCooldown.value = 60
      const timer = setInterval(() => {
        captchaCooldown.value--
        if (captchaCooldown.value <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    } else {
      ElMessage.error(response.message || '验证码发送失败')
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || '验证码发送失败'
    ElMessage.error(errorMsg)
  }
}

// 处理绑定
const handleBind = async () => {
  clearPhoneError()
  clearCaptchaError()
  loading.value = true

  if (!bindForm.phone) {
    phoneError.value = '请输入手机号'
    loading.value = false
    return
  }

  if (!bindForm.captcha) {
    captchaError.value = '请输入验证码'
    loading.value = false
    return
  }

  try {
    const response = await request.post('/auth/bind-phone/wechat', {
      phone: bindForm.phone,
      verifyCode: bindForm.captcha,
      sessionId: props.sessionId
    })

    if (response.code === '200') {
      // 保存token到localStorage
      localStorage.setItem('auth_token', response.data.token)

      eventBus.emitLogin()

      // ElMessage.success('手机绑定成功')
      emit('bind-success')
      dialogVisible.value = false
    } else {
      captchaError.value = response.message || '绑定失败，请重试'
    }
  } catch (error) {
    if (error.response?.data?.message) {
      if (error.response.data.message.includes('手机')) {
        phoneError.value = error.response.data.message
      } else if (error.response.data.message.includes('验证码')) {
        captchaError.value = error.response.data.message
      } else {
        ElMessage.error(error.response.data.message)
      }
    } else {
      ElMessage.error('绑定失败，请重试')
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.error-message {
  display: flex;
  align-items: center;
  color: #f84949;
  font-size: 12px;
  margin-top: 4px;
  height: 20px;

  .el-icon {
    margin-right: 4px;
    font-size: 14px;
  }
}

.success-message {
  display: flex;
  align-items: center;
  color: #67c23a;
  font-size: 12px;
  margin-top: 4px;
  height: 20px;

  .el-icon {
    margin-right: 4px;
    font-size: 14px;
  }
}

.wechat-bind-tip {
  margin-top: 10px;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  color: #606266;
  text-align: center;
}

.wechat-bind-tip p {
  margin: 5px 0;
}

.wechat-bind-tip strong {
  color: #f56c6c;
}

.captcha-btn {
  padding: 0;
  height: auto;
  color: #373737;
  font-size: 13px;
  white-space: nowrap;

  &:hover {
    color: #66b1ff;
  }

  &:disabled {
    color: #c0c4cc;
    cursor: not-allowed;
  }
}
</style>